import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '../../../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ContactBusinessHours, ContactLayout1Data } from '../layout_1/contact-layout-1.component';

export interface ContactLayout2Data {
  title?: string;
  subtitle?: string;
  address?: string;
  phone?: string;
  email?: string;
  mapEmbedUrl?: string;
  mapImage?: string;
  mapAlt?: string;
  businessHours?: ContactBusinessHours[];
  backgroundColor?: string;
}

/**
 * Preview component for Contact Layout 2 used in the website builder. Renders contact information, optional business hours, and a sanitized embedded map while applying provided styling overrides.
 * @component
 * @selector app-contact-layout-2-preview
 * @example
 *   <app-contact-layout-2-preview
 *     [data]="sampleContactData"
 *     [headingColor]="'#1a1a1a'"
 *     [paragraphColor]="'#666'"
 *     [headingFont]="'Roboto'"
 *     [bodyFont]="'Open Sans'"
 *     [primaryColor]="'#007bff'"
 *     [titleStyles]="{ 'fontWeight': '700' }">
 *   </app-contact-layout-2-preview>
 * @input {ContactLayout1Data | ContactLayout2Data | null} data - Data model containing contact fields, business hours array, and optional mapEmbedUrl used to render the contact layout. May be null.
 * @input {string | null} headingColor - Optional color override for headings.
 * @input {string | null} paragraphColor - Optional color override for body/paragraph text.
 * @input {string | null} headingFont - Optional font family for headings.
 * @input {string | null} bodyFont - Optional font family for body text.
 * @input {string | null} primaryColor - Optional primary color used for accents.
 * @input {any} titleStyles - Optional inline styles object applied to title elements (defaults to {}).
 * @output {none} none - This component does not emit output events; it is a presentational/preview component.
 *
 * Key methods:
 *  - hasBusinessHours(): boolean
 *      Returns true when the provided data contains a non-empty businessHours array. Used to conditionally render hours.
 *  - getMapEmbedUrl(): SafeResourceUrl | null
 *      Produces a sanitized, cached SafeResourceUrl for an embedded map. Handles raw iframe HTML or plain URLs:
 *        - Extracts src from iframe markup (including HTML-encoded variants).
 *        - Normalizes and rejects non-http(s) schemes (e.g., javascript:, data:).
 *        - Uses DomSanitizer.bypassSecurityTrustResourceUrl to create a SafeResourceUrl and caches it to avoid flicker on repeated calls.
 *      Returns null for invalid, missing, or unsafe map sources and logs extraction errors to the console.
 *
 * Important lifecycle / implementation notes:
 *  - Constructor injects Angular's DomSanitizer for URL sanitization.
 *  - No special Angular lifecycle hooks (e.g., ngOnInit) are required by this component; it computes values on demand.
 *  - Internally caches the last processed map URL and its sanitized SafeResourceUrl to prevent unnecessary re-sanitization and UI flicker when the same input is reused.
 */
@Component({
  selector: 'app-contact-layout-2-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './contact-layout-2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contact-layout-2.component.scss'],
})
export class ContactLayout2PreviewComponent {
  @Input() data: ContactLayout1Data | ContactLayout2Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};

  // Cache for map embed URL to prevent flickering
  private cachedMapEmbedUrl: SafeResourceUrl | null = null;
  private cachedMapEmbedUrlSource: string | null = null;

  constructor(
    private logger: LoggerService,
    private sanitizer: DomSanitizer
  ) {}

  /**
   * Check if business hours exist
   */
  hasBusinessHours(): boolean {
    return !!(
      this.data?.businessHours &&
      Array.isArray(this.data.businessHours) &&
      this.data.businessHours.length > 0
    );
  }

  /**
   * Get map embed URL (sanitized)
   */
  getMapEmbedUrl(): SafeResourceUrl | null {
    const mapEmbedUrl = this.data?.mapEmbedUrl;

    if (!mapEmbedUrl || typeof mapEmbedUrl !== 'string') {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    const trimmedUrl = mapEmbedUrl.trim();

    // Check if we have a cached value for this URL
    if (this.cachedMapEmbedUrl && this.cachedMapEmbedUrlSource === trimmedUrl) {
      return this.cachedMapEmbedUrl;
    }

    // Process the URL
    let processedUrl: string | null = trimmedUrl;

    // If it contains iframe HTML, extract the src URL
    if (trimmedUrl.includes('<iframe') || trimmedUrl.includes('&lt;iframe')) {
      try {
        const srcMatch = trimmedUrl.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i);
        if (srcMatch) {
          const extractedUrl = (srcMatch[1] || srcMatch[2] || '').trim();
          if (extractedUrl) {
            processedUrl = extractedUrl
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          }
        }

        if (!processedUrl || processedUrl === trimmedUrl) {
          const wrappedHtml = `<div>${trimmedUrl}</div>`;
          const parser = new DOMParser();
          const doc = parser.parseFromString(wrappedHtml, 'text/html');
          const iframe = doc.querySelector('iframe');
          if (iframe) {
            processedUrl = iframe.getAttribute('src');
          }
        }
      } catch (error) {
        this.logger.warn('Error extracting URL from iframe HTML:', error);
        this.cachedMapEmbedUrl = null;
        this.cachedMapEmbedUrlSource = null;
        return null;
      }
    }

    if (!processedUrl) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Only allow http(s) URLs — reject javascript:, data:, or any other scheme
    if (!processedUrl.startsWith('https://') && !processedUrl.startsWith('http://')) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Cache the sanitized URL
    this.cachedMapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(processedUrl);
    this.cachedMapEmbedUrlSource = trimmedUrl;

    return this.cachedMapEmbedUrl;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
